import { NavLink, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "./Toast.js";
import { apiFetch } from "../lib/api.js";
import { NotificationBell } from "./NotificationBell.js";
import { HelpWidget } from "./HelpWidget.js";
import { FeedbackButton } from "./FeedbackButton.js";
import { ErrorLogIndicator } from "./ErrorLogIndicator.js";

type FlatNavItem = { to: string; label: string };
type GroupedNavItems = { group: string; items: FlatNavItem[] };
type NavItem = FlatNavItem | GroupedNavItems;
type PagesNavNode = {
  id: string;
  label: string;
  icon: string | null;
  page: { page_id: string; route_key: string } | null;
  children: PagesNavNode[];
};
type PagesNavResponse =
  | { kind: "menu"; menu: { id: string; name: string }; nodes: PagesNavNode[] }
  | { kind: "fallback"; pages: Array<{ id: string; title: string; route_key: string }> };

// Inline SVG icons for tenant nav
function IconHome({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 10l7-7 7 7M5 10v7h10v-7" />
    </svg>
  );
}

function IconExplore({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="10" cy="10" r="8" />
      <path d="M10 2v4M10 14v4M2 10h4M14 10h4" />
    </svg>
  );
}

function IconPages({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 4h12v12H4z" />
      <path d="M4 8h12M8 4v12" />
    </svg>
  );
}

function IconSection({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <line x1="2" y1="3" x2="10" y2="3" />
      <line x1="2" y1="6" x2="10" y2="6" />
      <line x1="2" y1="9" x2="7" y2="9" />
    </svg>
  );
}

function IconBuild({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14.5 3.5a3.5 3.5 0 0 0-4.6 4.6L3 15v2h2l6.9-6.9a3.5 3.5 0 0 0 4.6-4.6l-2.3 2.3-2-2 2.3-2.3z" />
    </svg>
  );
}

// ADR-0068: database-cylinder icon for the Catalog group (renamed from Build).
function IconCatalog({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <ellipse cx="10" cy="4" rx="6" ry="2" />
      <path d="M4 4v12c0 1.1 2.69 2 6 2s6-0.9 6-2V4" />
      <path d="M4 10c0 1.1 2.69 2 6 2s6-0.9 6-2" />
    </svg>
  );
}

// ADR-0183 — Optimize nav icon (interleaved arrows: re-sequencing).
function IconOptimize({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 6h6l2 8h6" />
      <path d="M3 14h6l2-8h6" />
      <path d="M15 3.5L17.5 6L15 8.5" />
      <path d="M15 11.5L17.5 14L15 16.5" />
    </svg>
  );
}

// ADR-0143 — Studies nav icon (open book).
function IconStudies({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10 4.5C8.5 3.2 6.2 2.8 3 3v13c3.2-.2 5.5.2 7 1.5 1.5-1.3 3.8-1.7 7-1.5V3c-3.2-.2-5.5.2-7 1.5z" />
      <path d="M10 4.5V17.5" />
    </svg>
  );
}

// ADR-0220 — Initiatives nav icon (bars rising from a baseline; the mock-up's).
function IconInitiatives({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M3 14V8M8 14V4M13 14v-4" />
      <path d="M2 15h14" />
    </svg>
  );
}

// ADR-0221 — Issues nav icon (a circle with an exclamation; the mock-up's).
function IconIssues({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="9" cy="9" r="6.5" />
      <path d="M9 5.5v4M9 12.2v.3" />
    </svg>
  );
}

// ADR-0214 Part 2 — Subjects nav icon (a list with a person).
function IconSubjects({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 5h8M3 10h8M3 15h5" />
      <circle cx="15" cy="8" r="2.5" />
      <path d="M11.5 16c0-2 1.6-3.5 3.5-3.5s3.5 1.5 3.5 3.5" />
    </svg>
  );
}

function IconAccount({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="10" cy="7" r="4" />
      <path d="M3 18c0-3.314 3.134-6 7-6s7 2.686 7 6" />
    </svg>
  );
}

function IconHelp({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="10" cy="10" r="8" />
      <path d="M10 6v2M10 14h.01" />
    </svg>
  );
}

function IconAdmin({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10 2L3 7v11h4v-6h6v6h4V7l-7-5z" />
      <circle cx="10" cy="12" r="2" />
    </svg>
  );
}

function IconBuilding({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="4" width="16" height="14" rx="1" />
      <path d="M2 8h16" />
      <path d="M7 8v10M13 8v10" />
      <path d="M7 12h2M11 12h2M7 16h2M11 16h2" strokeLinecap="round" />
    </svg>
  );
}

function IconTeam({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="8" cy="6" r="3" />
      <path d="M2 18c0-3 2.5-5 6-5s6 2 6 5" />
      <circle cx="15" cy="7" r="2" />
      <path d="M15 11c2.5 0.5 4 2 4 4" />
    </svg>
  );
}

function IconTransfer({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 7h11M11 4l3 3-3 3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M17 13H6M8 10l-3 3 3 3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconOrphaned({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10 3L2 17h16L10 3z" strokeLinejoin="round" />
      <path d="M10 9v4" strokeLinecap="round" />
      <circle cx="10" cy="15" r="0.5" fill="currentColor" />
    </svg>
  );
}

function IconConnector({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M7 3v4M13 3v4" />
      <path d="M6 7h8v3a4 4 0 0 1-8 0V7z" />
      <path d="M10 14v3M7 17h6" />
    </svg>
  );
}

function IconUsage({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 16V10M10 16V4M16 16v-6" />
    </svg>
  );
}

function IconTenantKnowledge({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 4h14v12H3z" />
      <path d="M6 8h8M6 11h5" />
      <circle cx="15" cy="14" r="3" />
      <path d="M17 16l1.5 1.5" />
    </svg>
  );
}

function IconKnowledge({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 3h12a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V4a1 1 0 011-1z" />
      <path d="M7 7h6M7 10h6M7 13h4" />
    </svg>
  );
}

function IconSignOut({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M13 3h4a2 2 0 012 2v10a2 2 0 01-2 2h-4M8 15l5-5-5-5M13 10H3" />
    </svg>
  );
}

function IconSchedule({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="10" cy="11" r="7" />
      <path d="M10 7v4l2.5 2.5" />
      <path d="M7 2h6" strokeLinecap="round" />
      <path d="M10 2v2" />
    </svg>
  );
}

function IconActionItems({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 5h12M4 10h12M4 15h8" strokeLinecap="round" />
      <path d="M2 5l1 1 1-1M2 10l1 1 1-1M2 15l1 1 1-1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconMyItems({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="10" cy="6" r="3" />
      <path d="M4 17c0-3 3-5 6-5s6 2 6 5" />
      <path d="M13 15l1.5 1.5L18 13" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconChevron({ size = 18, expanded = false }: { size?: number; expanded?: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      style={{ transform: expanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 150ms ease" }}
    >
      <path d="M7 5l5 5-5 5" />
    </svg>
  );
}

function IconRailToggle({ size = 18, collapsed = false }: { size?: number; collapsed?: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      style={{ transform: collapsed ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 150ms ease" }}
    >
      <path d="M7 5l5 5-5 5" />
    </svg>
  );
}

function EnvBadge() {
  const mode = import.meta.env.MODE;
  const label = mode === "production" ? "Prod" : mode;
  return <span className="envPill">env: {label}</span>;
}

function RoleBadge({ entitlement }: { entitlement?: string }) {
  const roleLabel = entitlement === "admin" ? "Admin" : entitlement === "builder" ? "Builder" : "Viewer";
  return <span className="envPill">{roleLabel}</span>;
}

export function AppShell(props: {
  contextLabel: string;
  navItems: NavItem[];
  userEmail?: string;
  accountPath?: string | null;
  logoutRedirect?: string;
  navVariant?: "admin" | "tenant";
  tenantId?: string;
  userId?: string;
  tenantEntitlement?: string;
  /** ADR-0185 — content-driven: the Optimize entry opens to every role once the tenant has an active model the caller can read. */
  optimizeAvailable?: boolean;
  /** ADR-0213 ruling 84 — Atlas Query is switched on for this person (`lib/atlasQueryAccess.ts`). */
  atlasQueryAvailable?: boolean;
  children: ReactNode;
}) {
  const nav = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const accountPath = props.accountPath === undefined ? "/admin/account" : props.accountPath;
  const logoutRedirect = props.logoutRedirect ?? "/login";
  const navVariant = props.navVariant ?? "admin";

  // Generate tenant-scoped localStorage keys
  function getStorageKey(key: string): string {
    if (navVariant !== "tenant" || !props.tenantId || !props.userId) {
      return `atlas.console.tenantNav.v0.${key}`;
    }
    return `atlas.console.tenantNav.v0.${props.tenantId}.${props.userId}.${key}`;
  }

  // Tenant nav state
  const [railCollapsed, setRailCollapsed] = useState(() => {
    if (navVariant !== "tenant") return false;
    const stored = localStorage.getItem(getStorageKey("railCollapsed"));
    return stored === "1";
  });

  const [groupExpanded, setGroupExpanded] = useState<Record<string, boolean>>(() => {
    if (navVariant !== "tenant") return {};
    const stored = localStorage.getItem(getStorageKey("groupExpanded"));
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return {};
      }
    }
    return {};
  });

  // Build fly-out state (for rail mode)
  const [contentFlyoutOpen, setContentFlyoutOpen] = useState(false);
  const contentButtonRef = useRef<HTMLButtonElement | null>(null);
  const contentFlyoutRef = useRef<HTMLDivElement | null>(null);
  // ADR-0068 Data group fly-out state (renamed from Workspace)
  const [dataFlyoutOpen, setDataFlyoutOpen] = useState(false);
  const dataButtonRef = useRef<HTMLButtonElement | null>(null);
  const dataFlyoutRef = useRef<HTMLDivElement | null>(null);
  // Admin Content sub-group expansion state
  const [contentAdminExpanded, setContentAdminExpanded] = useState(() => {
    if (navVariant !== "tenant") return true;
    try {
      return localStorage.getItem(getStorageKey("contentAdminExpanded")) !== "0";
    } catch {
      return true;
    }
  });
  // Admin Tenant sub-group expansion state
  const [tenantAdminExpanded, setTenantAdminExpanded] = useState(() => {
    if (navVariant !== "tenant") return true;
    try {
      return localStorage.getItem(getStorageKey("tenantAdminExpanded")) !== "0";
    } catch {
      return true;
    }
  });
  // ADR-0068 Admin AI sub-group expansion state
  const [aiAdminExpanded, setAiAdminExpanded] = useState(() => {
    if (navVariant !== "tenant") return true;
    try {
      return localStorage.getItem(getStorageKey("aiAdminExpanded")) !== "0";
    } catch {
      return true;
    }
  });
  // Schedule fly-out state (for rail mode)
  const [scheduleFlyoutOpen, setScheduleFlyoutOpen] = useState(false);
  const scheduleButtonRef = useRef<HTMLButtonElement | null>(null);
  const scheduleFlyoutRef = useRef<HTMLDivElement | null>(null);
  // Pages fly-out state (for rail mode)
  const [pagesFlyoutOpen, setPagesFlyoutOpen] = useState(false);
  const pagesButtonRef = useRef<HTMLButtonElement | null>(null);
  const pagesFlyoutRef = useRef<HTMLDivElement | null>(null);
  const [pagesNavLoading, setPagesNavLoading] = useState(false);
  const [pagesNavError, setPagesNavError] = useState<string | null>(null);
  const [pagesNav, setPagesNav] = useState<PagesNavResponse | null>(null);
  const [pagesGroupExpanded, setPagesGroupExpanded] = useState(() => {
    if (navVariant !== "tenant") return true;
    const stored = localStorage.getItem(getStorageKey("pagesGroupExpanded"));
    return stored !== "0";
  });
  const [pagesOpenIds, setPagesOpenIds] = useState<string[]>(() => {
    if (navVariant !== "tenant") return [];
    const stored = localStorage.getItem(getStorageKey("pagesOpenIds"));
    if (!stored) return [];
    try {
      return JSON.parse(stored);
    } catch {
      return [];
    }
  });

  // Tooltip state (for rail mode)
  const [tooltipContent, setTooltipContent] = useState<string | null>(null);
  const [tooltipTarget, setTooltipTarget] = useState<HTMLElement | null>(null);

  // Auto-expand Build if active route is inside it
  useEffect(() => {
    if (navVariant !== "tenant") return;
    if (location.pathname.startsWith("/app/build/") || location.pathname === "/app/atlas-query") {
      setGroupExpanded((prev) => {
        const updated = { ...prev, Build: true };
        localStorage.setItem(getStorageKey("groupExpanded"), JSON.stringify(updated));
        return updated;
      });
    }
    if (location.pathname.startsWith("/app/schedule")) {
      setGroupExpanded((prev) => {
        const updated = { ...prev, Schedule: true };
        localStorage.setItem(getStorageKey("groupExpanded"), JSON.stringify(updated));
        return updated;
      });
    }
  }, [location.pathname, navVariant, props.tenantId, props.userId]);

  // Persist rail state
  useEffect(() => {
    if (navVariant !== "tenant") return;
    localStorage.setItem(getStorageKey("railCollapsed"), railCollapsed ? "1" : "0");
  }, [railCollapsed, navVariant, props.tenantId, props.userId]);

  // Persist group expansion
  useEffect(() => {
    if (navVariant !== "tenant") return;
    localStorage.setItem(getStorageKey("groupExpanded"), JSON.stringify(groupExpanded));
  }, [groupExpanded, navVariant, props.tenantId, props.userId]);

  useEffect(() => {
    if (navVariant !== "tenant") return;
    localStorage.setItem(getStorageKey("pagesGroupExpanded"), pagesGroupExpanded ? "1" : "0");
  }, [pagesGroupExpanded, navVariant, props.tenantId, props.userId]);

  useEffect(() => {
    if (navVariant !== "tenant") return;
    localStorage.setItem(getStorageKey("pagesOpenIds"), JSON.stringify(pagesOpenIds));
  }, [pagesOpenIds, navVariant, props.tenantId, props.userId]);

  useEffect(() => {
    if (navVariant !== "tenant") return;
    let mounted = true;
    async function loadPagesNav(showLoading: boolean) {
      if (showLoading) {
        setPagesNavLoading(true);
      }
      setPagesNavError(null);
      try {
        const data = await apiFetch<PagesNavResponse>("/api/tenant/nav/pages");
        if (!mounted) return;
        setPagesNav(data);
      } catch (e) {
        if (!mounted) return;
        setPagesNavError(e instanceof Error ? e.message : String(e));
      } finally {
        if (mounted) setPagesNavLoading(false);
      }
    }
    void loadPagesNav(true);
    const interval = window.setInterval(() => {
      void loadPagesNav(false);
    }, 5000);
    const onFocus = () => {
      void loadPagesNav(false);
    };
    window.addEventListener("focus", onFocus);
    return () => {
      mounted = false;
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [navVariant, props.tenantId, props.userId]);

  function toggleGroup(groupName: string) {
    if (railCollapsed && groupName === "Content") {
      // In rail mode, toggle fly-out instead
      setContentFlyoutOpen((prev) => {
        const newState = !prev;
        if (newState) {
          setTimeout(() => {
            const firstItem = contentFlyoutRef.current?.querySelector<HTMLElement>(".tenantNavFlyoutItem");
            firstItem?.focus();
          }, 0);
        }
        return newState;
      });
    } else if (railCollapsed && groupName === "Data") {
      setDataFlyoutOpen((prev) => {
        const newState = !prev;
        if (newState) {
          setTimeout(() => {
            const firstItem = dataFlyoutRef.current?.querySelector<HTMLElement>(".tenantNavFlyoutItem");
            firstItem?.focus();
          }, 0);
        }
        return newState;
      });
    } else if (railCollapsed && groupName === "Schedule") {
      setScheduleFlyoutOpen((prev) => {
        const newState = !prev;
        if (newState) {
          setTimeout(() => {
            const firstItem = scheduleFlyoutRef.current?.querySelector<HTMLElement>(".tenantNavFlyoutItem");
            firstItem?.focus();
          }, 0);
        }
        return newState;
      });
    } else if (groupName === "Pages") {
      setPagesGroupExpanded((prev) => !prev);
    } else {
      setGroupExpanded((prev) => ({ ...prev, [groupName]: !prev[groupName] }));
    }
  }

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node | null;
      if (!t) return;
      
      // Handle user menu
      if (open && !panelRef.current?.contains(t)) {
        setOpen(false);
      }
      
      // Handle Build fly-out (only in rail mode)
      if (contentFlyoutOpen && railCollapsed) {
        if (!contentButtonRef.current?.contains(t) && !contentFlyoutRef.current?.contains(t)) {
          setContentFlyoutOpen(false);
        }
      }
      if (scheduleFlyoutOpen && railCollapsed) {
        if (!scheduleButtonRef.current?.contains(t) && !scheduleFlyoutRef.current?.contains(t)) {
          setScheduleFlyoutOpen(false);
        }
      }
      if (pagesFlyoutOpen && railCollapsed) {
        if (!pagesButtonRef.current?.contains(t) && !pagesFlyoutRef.current?.contains(t)) {
          setPagesFlyoutOpen(false);
        }
      }
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [open, contentFlyoutOpen, scheduleFlyoutOpen, pagesFlyoutOpen, railCollapsed]);

  async function logout() {
    try {
      await apiFetch("/api/auth/logout", { method: "POST" });
      nav(logoutRedirect);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  function togglePageNode(nodeId: string) {
    setPagesOpenIds((prev) => (prev.includes(nodeId) ? prev.filter((id) => id !== nodeId) : [...prev, nodeId]));
  }

  function isPageRouteActive(routeKey: string): boolean {
    return location.pathname === `/app/pages/${encodeURIComponent(routeKey)}`;
  }

  function renderPagesNodes(nodes: PagesNavNode[], depth: number): ReactNode {
    return nodes.map((node) => {
      const hasChildren = node.children.length > 0;
      const expanded = pagesOpenIds.includes(node.id);
      const active = node.page ? isPageRouteActive(node.page.route_key) : false;
      return (
        <div key={node.id}>
          <div
            className={`tenantNavRow tenantNavSubRow ${node.page ? "tenantNavLeaf" : "tenantNavFolder"} ${active ? "tenantNavActive" : ""}`}
            style={{ paddingLeft: `${16 + depth * 16}px` }}
          >
            {node.page ? (
              <NavLink to={`/app/pages/${encodeURIComponent(node.page.route_key)}`} className="tenantNavLabel" title={node.label}>
                {node.label}
              </NavLink>
            ) : (
              <button
                type="button"
                className="tenantNavLabel tenantNavLabelBtn"
                onClick={() => hasChildren && togglePageNode(node.id)}
                title={node.label}
              >
                <span className="tenantNavCaret" aria-hidden="true">
                  <IconSection size={12} />
                </span>
                {node.label}
              </button>
            )}
            {hasChildren ? (
              <button
                type="button"
                className="tenantNavChevronBtn"
                onClick={() => togglePageNode(node.id)}
                aria-label={expanded ? "Collapse pages item" : "Expand pages item"}
              >
                <IconChevron size={14} expanded={expanded} />
              </button>
            ) : null}
          </div>
          {hasChildren && expanded ? renderPagesNodes(node.children, depth + 1) : null}
        </div>
      );
    });
  }

  return (
    <div>
      <div className="topbar">
        <div className="brand">
          <div className="brandName">Atlas</div>
          <span className="badge">{props.contextLabel}</span>
        </div>
        <div className="toolbarGroup">
          {navVariant === "tenant" ? (
            <RoleBadge entitlement={props.tenantEntitlement} />
          ) : (
            <EnvBadge />
          )}
          <ErrorLogIndicator />
          {navVariant === "tenant" && <NotificationBell />}
          {navVariant === "tenant" && <FeedbackButton />}
          {navVariant === "tenant" && <HelpWidget />}
          <div className="userMenu" ref={panelRef}>
            <button className="menuButton" onClick={() => setOpen((v) => !v)} type="button">
              <span className="muted">{props.userEmail ?? "User"}</span>
              <span className="muted">▾</span>
            </button>
            {open && (
              <div className="menuPanel">
                <div className="muted" style={{ padding: "6px 10px", fontSize: 12 }}>
                  Signed in as
                </div>
                <div style={{ padding: "0 10px 8px 10px", fontWeight: 700 }}>{props.userEmail ?? "unknown"}</div>
                {accountPath ? (
                  <button
                    className="menuItem"
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      nav(accountPath);
                    }}
                  >
                    Account
                  </button>
                ) : null}
                <button className="menuItem" type="button" onClick={() => void logout()}>
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={`shell ${navVariant === "tenant" && railCollapsed ? "shellNavCollapsed" : ""}`}>
        <aside className={navVariant === "tenant" ? `tenantNav ${railCollapsed ? "tenantNavRail" : ""}` : "nav"}>
          {navVariant === "tenant" ? (
            <>
              {/* Rail toggle */}
              <div className="tenantNavToggle">
                <button
                  type="button"
                  onClick={() => setRailCollapsed((v) => !v)}
                  className="tenantNavToggleButton"
                  aria-label={railCollapsed ? "Expand navigation" : "Collapse navigation"}
                >
                  <IconRailToggle size={18} collapsed={railCollapsed} />
                </button>
              </div>

              {/* Top section */}
              <div className="tenantNavSection">
                <NavLink
                  to="/app/home"
                  end
                  className={`tenantNavRow ${location.pathname.startsWith("/app/home") ? "tenantNavActive" : ""}`}
                  title="Home"
                  onMouseEnter={(e) => {
                    if (railCollapsed) {
                      setTooltipContent("Home");
                      setTooltipTarget(e.currentTarget);
                    }
                  }}
                  onMouseLeave={() => {
                    if (railCollapsed) {
                      setTooltipContent(null);
                      setTooltipTarget(null);
                    }
                  }}
                >
                  <span className="tenantNavIcon">
                    <IconHome size={18} />
                  </span>
                  {!railCollapsed && <span className="tenantNavLabel">Home</span>}
                </NavLink>
                <div className="tenantNavGroupWrapper navAccentPages">
                  <button
                    ref={pagesButtonRef}
                    type="button"
                    className={`tenantNavGroupHeader ${location.pathname.startsWith("/app/pages/") ? "tenantNavActive" : ""}`}
                    onClick={() => {
                      if (railCollapsed) {
                        setPagesFlyoutOpen((prev) => !prev);
                      } else {
                        toggleGroup("Pages");
                      }
                    }}
                    onMouseEnter={() => {
                      if (railCollapsed) {
                        setPagesFlyoutOpen(true);
                      }
                    }}
                    onMouseLeave={() => {
                      if (railCollapsed) {
                        setTimeout(() => {
                          if (!pagesFlyoutRef.current?.matches(":hover")) {
                            setPagesFlyoutOpen(false);
                          }
                        }, 100);
                      }
                    }}
                    aria-expanded={railCollapsed ? pagesFlyoutOpen : pagesGroupExpanded}
                  >
                    <span className="tenantNavIcon">
                      <IconPages size={18} />
                    </span>
                    {!railCollapsed && (
                      <>
                        <span className="tenantNavLabel">Pages</span>
                        <IconChevron size={16} expanded={pagesGroupExpanded} />
                      </>
                    )}
                  </button>
                  {!railCollapsed && pagesGroupExpanded ? (
                    <div className="tenantNavChildren">
                      {pagesNavLoading ? <div className="tenantNavRow tenantNavSubRow">Loading...</div> : null}
                      {!pagesNavLoading && pagesNavError ? <div className="tenantNavRow tenantNavSubRow">Pages unavailable</div> : null}
                      {!pagesNavLoading && !pagesNavError && pagesNav?.kind === "fallback" ? (
                        <div className="tenantNavRow tenantNavSubRow">No published menu</div>
                      ) : null}
                      {!pagesNavLoading && !pagesNavError && pagesNav?.kind === "menu" ? renderPagesNodes(pagesNav.nodes, 0) : null}
                    </div>
                  ) : null}
                  {railCollapsed && pagesFlyoutOpen ? (
                    <div
                      ref={pagesFlyoutRef}
                      className="tenantNavFlyout"
                      onMouseEnter={() => setPagesFlyoutOpen(true)}
                      onMouseLeave={() => setPagesFlyoutOpen(false)}
                    >
                      {pagesNavLoading ? <div className="tenantNavFlyoutItem">Loading...</div> : null}
                      {!pagesNavLoading && pagesNavError ? <div className="tenantNavFlyoutItem">Pages unavailable</div> : null}
                      {!pagesNavLoading && !pagesNavError && pagesNav?.kind === "fallback" ? (
                        <div className="tenantNavFlyoutItem">No published menu</div>
                      ) : null}
                      {!pagesNavLoading && !pagesNavError && pagesNav?.kind === "menu"
                        ? pagesNav.nodes.map((node) => (
                            <div key={node.id} className="tenantNavFlyoutItem">
                              {node.page ? (
                                <NavLink to={`/app/pages/${encodeURIComponent(node.page.route_key)}`} onClick={() => setPagesFlyoutOpen(false)}>
                                  {node.label}
                                </NavLink>
                              ) : (
                                node.label
                              )}
                            </div>
                          ))
                        : null}
                    </div>
                  ) : null}
                </div>
                {/* ADR-0068 Data group — Providers, Operations, PDOs, Semantic Models, Atlas Query */}
                <div className="tenantNavGroupWrapper navAccentData">
                  <button
                    ref={dataButtonRef}
                    type="button"
                    className={`tenantNavGroupHeader ${
                      location.pathname.startsWith("/app/build/providers") ||
                      location.pathname.startsWith("/app/build/operations") ||
                      location.pathname.startsWith("/app/build/semantic-models") ||
                      location.pathname.startsWith("/app/workspace/pdos") ||
                      location.pathname.startsWith("/app/workspace/models") ||
                      location.pathname.startsWith("/app/build/models") ||
                      location.pathname === "/app/atlas-query"
                        ? "tenantNavActive" : ""
                    }`}
                    onClick={() => toggleGroup("Data")}
                    onMouseEnter={() => { if (railCollapsed) setDataFlyoutOpen(true); }}
                    onMouseLeave={() => {
                      if (railCollapsed) {
                        setTimeout(() => {
                          if (!dataFlyoutRef.current?.matches(":hover")) setDataFlyoutOpen(false);
                        }, 100);
                      }
                    }}
                    aria-expanded={railCollapsed ? dataFlyoutOpen : groupExpanded.Data ?? false}
                    aria-controls="tenantNavDataChildren"
                  >
                    <span className="tenantNavIcon"><IconCatalog size={18} /></span>
                    {!railCollapsed && (
                      <>
                        <span className="tenantNavLabel">Data</span>
                        <IconChevron size={16} expanded={groupExpanded.Data ?? false} />
                      </>
                    )}
                  </button>
                  {!railCollapsed && (groupExpanded.Data ?? false) && (
                    <div id="tenantNavDataChildren" className="tenantNavChildren">
                      {(props.tenantEntitlement === "builder" || props.tenantEntitlement === "admin") && (
                        <NavLink to="/app/build/providers" className="tenantNavRow tenantNavSubRow" title="Data Providers">
                          <span className="tenantNavLabel">Data Providers</span>
                        </NavLink>
                      )}
                      {(props.tenantEntitlement === "builder" || props.tenantEntitlement === "admin") && (
                        <NavLink to="/app/build/operations" className="tenantNavRow tenantNavSubRow" title="Operations">
                          <span className="tenantNavLabel">Operations</span>
                        </NavLink>
                      )}
                      {(props.tenantEntitlement === "builder" || props.tenantEntitlement === "admin") && (
                        <NavLink to="/app/workspace/pdos" className="tenantNavRow tenantNavSubRow" title="Persistent Data Objects">
                          <span className="tenantNavLabel">PDOs</span>
                        </NavLink>
                      )}
                      {(props.tenantEntitlement === "builder" || props.tenantEntitlement === "admin") && (
                        <NavLink to="/app/workspace/models" className="tenantNavRow tenantNavSubRow" title="Models">
                          <span className="tenantNavLabel">Models</span>
                        </NavLink>
                      )}
                      {/* Editing the semantic model is admin-only: placing an object in a subject area grants access. */}
                      {props.tenantEntitlement === "admin" && (
                        <NavLink to="/app/build/semantic-models" className="tenantNavRow tenantNavSubRow" title="Semantic Models">
                          <span className="tenantNavLabel">Semantic Models</span>
                        </NavLink>
                      )}
                      {props.atlasQueryAvailable && (
                        <NavLink to="/app/atlas-query" className="tenantNavRow tenantNavSubRow" title="Atlas Query">
                          <span className="tenantNavLabel">Atlas Query</span>
                        </NavLink>
                      )}
                    </div>
                  )}
                  {railCollapsed && dataFlyoutOpen && (
                    <div
                      ref={dataFlyoutRef}
                      className="tenantNavFlyout"
                      onMouseEnter={() => setDataFlyoutOpen(true)}
                      onMouseLeave={() => setDataFlyoutOpen(false)}
                      role="menu"
                    >
                      {(props.tenantEntitlement === "builder" || props.tenantEntitlement === "admin") && (
                        <NavLink to="/app/build/providers" className="tenantNavFlyoutItem" onClick={() => setDataFlyoutOpen(false)} role="menuitem" tabIndex={0}>
                          Data Providers
                        </NavLink>
                      )}
                      {(props.tenantEntitlement === "builder" || props.tenantEntitlement === "admin") && (
                        <NavLink to="/app/build/operations" className="tenantNavFlyoutItem" onClick={() => setDataFlyoutOpen(false)} role="menuitem" tabIndex={0}>
                          Operations
                        </NavLink>
                      )}
                      {(props.tenantEntitlement === "builder" || props.tenantEntitlement === "admin") && (
                        <NavLink to="/app/workspace/pdos" className="tenantNavFlyoutItem" onClick={() => setDataFlyoutOpen(false)} role="menuitem" tabIndex={0}>
                          PDOs
                        </NavLink>
                      )}
                      {(props.tenantEntitlement === "builder" || props.tenantEntitlement === "admin") && (
                        <NavLink to="/app/workspace/models" className="tenantNavFlyoutItem" onClick={() => setDataFlyoutOpen(false)} role="menuitem" tabIndex={0}>
                          Models
                        </NavLink>
                      )}
                      {props.tenantEntitlement === "admin" && (
                        <NavLink to="/app/build/semantic-models" className="tenantNavFlyoutItem" onClick={() => setDataFlyoutOpen(false)} role="menuitem" tabIndex={0}>
                          Semantic Models
                        </NavLink>
                      )}
                      {props.atlasQueryAvailable && (
                        <NavLink to="/app/atlas-query" className="tenantNavFlyoutItem" onClick={() => setDataFlyoutOpen(false)} role="menuitem" tabIndex={0}>
                          Atlas Query
                        </NavLink>
                      )}
                    </div>
                  )}
                </div>
                {/* Build → Catalog group (ADR-0068): shared-only infrastructure */}
                {/* ADR-0068 Content group — Pages + Menus. Builder/admin only; viewers have no content management or rendering. */}
                {(props.tenantEntitlement === "builder" || props.tenantEntitlement === "admin") && (
                <div className="tenantNavGroupWrapper navAccentContent">
                  <button
                    ref={contentButtonRef}
                    type="button"
                    className={`tenantNavGroupHeader ${
                      location.pathname.startsWith("/app/workspace/pages") ||
                      location.pathname.startsWith("/app/workspace/menus") ||
                      location.pathname.startsWith("/app/workspace/atlas-reports")
                        ? "tenantNavActive" : ""
                    }`}
                    onClick={() => toggleGroup("Content")}
                    onMouseEnter={() => { if (railCollapsed) setContentFlyoutOpen(true); }}
                    onMouseLeave={() => {
                      if (railCollapsed) {
                        setTimeout(() => {
                          if (!contentFlyoutRef.current?.matches(":hover")) setContentFlyoutOpen(false);
                        }, 100);
                      }
                    }}
                    aria-expanded={railCollapsed ? contentFlyoutOpen : groupExpanded.Content ?? false}
                    aria-controls="tenantNavContentChildren"
                  >
                    <span className="tenantNavIcon"><IconBuild size={18} /></span>
                    {!railCollapsed && (
                      <>
                        <span className="tenantNavLabel">Content</span>
                        <IconChevron size={16} expanded={groupExpanded.Content ?? false} />
                      </>
                    )}
                  </button>
                  {!railCollapsed && (groupExpanded.Content ?? false) && (
                    <div id="tenantNavContentChildren" className="tenantNavChildren">
                      <NavLink to="/app/workspace/pages" className="tenantNavRow tenantNavSubRow" title="Pages">
                        <span className="tenantNavLabel">Pages</span>
                      </NavLink>
                      <NavLink to="/app/workspace/menus" className="tenantNavRow tenantNavSubRow" title="Menus">
                        <span className="tenantNavLabel">Menus</span>
                      </NavLink>
                      <NavLink to="/app/workspace/atlas-reports" className="tenantNavRow tenantNavSubRow" title="Atlas Reports">
                        <span className="tenantNavLabel">Atlas Reports</span>
                      </NavLink>
                    </div>
                  )}
                  {railCollapsed && contentFlyoutOpen && (
                    <div
                      ref={contentFlyoutRef}
                      className="tenantNavFlyout"
                      onMouseEnter={() => setContentFlyoutOpen(true)}
                      onMouseLeave={() => setContentFlyoutOpen(false)}
                      role="menu"
                    >
                      <NavLink to="/app/workspace/pages" className="tenantNavFlyoutItem" onClick={() => setContentFlyoutOpen(false)} role="menuitem" tabIndex={0}>
                        Pages
                      </NavLink>
                      <NavLink to="/app/workspace/menus" className="tenantNavFlyoutItem" onClick={() => setContentFlyoutOpen(false)} role="menuitem" tabIndex={0}>
                        Menus
                      </NavLink>
                      <NavLink to="/app/workspace/atlas-reports" className="tenantNavFlyoutItem" onClick={() => setContentFlyoutOpen(false)} role="menuitem" tabIndex={0}>
                        Atlas Reports
                      </NavLink>
                    </div>
                  )}
                </div>
                )}
                {/* Schedule group — builder/admin only. Viewers have no access to schedules. */}
                {(props.tenantEntitlement === "builder" || props.tenantEntitlement === "admin") && (
                <div className="tenantNavGroupWrapper navAccentSchedule">
                    <button
                      ref={scheduleButtonRef}
                      type="button"
                      className={`tenantNavGroupHeader ${
                        location.pathname.startsWith("/app/schedule") ? "tenantNavActive" : ""
                      }`}
                      onClick={() => toggleGroup("Schedule")}
                      onMouseEnter={() => {
                        if (railCollapsed) setScheduleFlyoutOpen(true);
                      }}
                      onMouseLeave={() => {
                        if (railCollapsed) {
                          setTimeout(() => {
                            if (!scheduleFlyoutRef.current?.matches(":hover")) {
                              setScheduleFlyoutOpen(false);
                            }
                          }, 100);
                        }
                      }}
                      aria-expanded={railCollapsed ? scheduleFlyoutOpen : groupExpanded.Schedule ?? false}
                      aria-controls="tenantNavScheduleChildren"
                    >
                      <span className="tenantNavIcon">
                        <IconSchedule size={18} />
                      </span>
                      {!railCollapsed && (
                        <>
                          <span className="tenantNavLabel">Schedule</span>
                          <IconChevron size={16} expanded={groupExpanded.Schedule ?? false} />
                        </>
                      )}
                    </button>
                    {!railCollapsed && (groupExpanded.Schedule ?? false) && (
                      <div id="tenantNavScheduleChildren" className="tenantNavChildren">
                        {/* ADR-0068: "Schedules" is the scope-aware workflow list (renamed from Workflows) */}
                        <NavLink to="/app/workspace/schedules" className="tenantNavRow tenantNavSubRow" title="Schedules">
                          <span className="tenantNavLabel">Schedules</span>
                        </NavLink>
                        <NavLink to="/app/schedule/blackout-schedules" className="tenantNavRow tenantNavSubRow" title="Blackouts">
                          <span className="tenantNavLabel">Blackouts</span>
                        </NavLink>
                        <NavLink to="/app/schedule/activity" className="tenantNavRow tenantNavSubRow" title="Activity">
                          <span className="tenantNavLabel">Activity</span>
                        </NavLink>
                        {/* ADR-0098 */}
                        <NavLink to="/app/schedule/subscriptions" className="tenantNavRow tenantNavSubRow" title="Subscriptions">
                          <span className="tenantNavLabel">Subscriptions</span>
                        </NavLink>
                        <NavLink to="/app/monitor/watches" className="tenantNavRow tenantNavSubRow" title="Watches">
                          <span className="tenantNavLabel">Watches</span>
                        </NavLink>
                      </div>
                    )}
                    {/* Fly-out for rail mode */}
                    {railCollapsed && scheduleFlyoutOpen && (
                      <div
                        ref={scheduleFlyoutRef}
                        className="tenantNavFlyout"
                        onMouseEnter={() => setScheduleFlyoutOpen(true)}
                        onMouseLeave={() => setScheduleFlyoutOpen(false)}
                        role="menu"
                      >
                        <NavLink
                          to="/app/workspace/schedules"
                          className="tenantNavFlyoutItem"
                          onClick={() => setScheduleFlyoutOpen(false)}
                          role="menuitem"
                          tabIndex={0}
                        >
                          Schedules
                        </NavLink>
                        <NavLink
                          to="/app/schedule/blackout-schedules"
                          className="tenantNavFlyoutItem"
                          onClick={() => setScheduleFlyoutOpen(false)}
                          role="menuitem"
                          tabIndex={0}
                        >
                          Blackouts
                        </NavLink>
                        <NavLink
                          to="/app/schedule/activity"
                          className="tenantNavFlyoutItem"
                          onClick={() => setScheduleFlyoutOpen(false)}
                          role="menuitem"
                          tabIndex={0}
                        >
                          Activity
                        </NavLink>
                        {/* ADR-0098 */}
                        <NavLink
                          to="/app/schedule/subscriptions"
                          className="tenantNavFlyoutItem"
                          onClick={() => setScheduleFlyoutOpen(false)}
                          role="menuitem"
                          tabIndex={0}
                        >
                          Subscriptions
                        </NavLink>
                        <NavLink
                          to="/app/monitor/watches"
                          className="tenantNavFlyoutItem"
                          onClick={() => setScheduleFlyoutOpen(false)}
                          role="menuitem"
                          tabIndex={0}
                        >
                          Watches
                        </NavLink>
                      </div>
                    )}
                  </div>
                )}
                <NavLink
                  to="/app/action-items"
                  className="tenantNavRow"
                  title="Action Items"
                  onMouseEnter={(e) => {
                    if (railCollapsed) {
                      setTooltipContent("Action Items");
                      setTooltipTarget(e.currentTarget);
                    }
                  }}
                  onMouseLeave={() => {
                    if (railCollapsed) {
                      setTooltipContent(null);
                      setTooltipTarget(null);
                    }
                  }}
                >
                  <span className="tenantNavIcon">
                    <IconActionItems size={18} />
                  </span>
                  {!railCollapsed && <span className="tenantNavLabel">Action Items</span>}
                </NavLink>
                {/* ADR-0068: "My Action Items" removed as separate nav — now a Mine/All toggle on the Action Items list */}
                {/* ADR-0143 — Studies: every membership role; the list is scope-filtered server-side. */}
                <NavLink
                  to="/app/studies"
                  className="tenantNavRow"
                  title="Studies"
                  onMouseEnter={(e) => {
                    if (railCollapsed) {
                      setTooltipContent("Studies");
                      setTooltipTarget(e.currentTarget);
                    }
                  }}
                  onMouseLeave={() => {
                    if (railCollapsed) {
                      setTooltipContent(null);
                      setTooltipTarget(null);
                    }
                  }}
                >
                  <span className="tenantNavIcon">
                    <IconStudies size={18} />
                  </span>
                  {!railCollapsed && <span className="tenantNavLabel">Studies</span>}
                </NavLink>
                {/* ADR-0220 — Initiatives: every membership role; one row for the list and the rail (spec E15). */}
                <NavLink
                  to="/app/initiatives"
                  className="tenantNavRow"
                  title="Initiatives"
                  onMouseEnter={(e) => {
                    if (railCollapsed) {
                      setTooltipContent("Initiatives");
                      setTooltipTarget(e.currentTarget);
                    }
                  }}
                  onMouseLeave={() => {
                    if (railCollapsed) {
                      setTooltipContent(null);
                      setTooltipTarget(null);
                    }
                  }}
                >
                  <span className="tenantNavIcon">
                    <IconInitiatives size={18} />
                  </span>
                  {!railCollapsed && <span className="tenantNavLabel">Initiatives</span>}
                </NavLink>
                {/* ADR-0221 — Issues: every membership role; directly after Initiatives (spec §3.1). */}
                <NavLink
                  to="/app/issues"
                  className="tenantNavRow"
                  title="Issues"
                  onMouseEnter={(e) => {
                    if (railCollapsed) {
                      setTooltipContent("Issues");
                      setTooltipTarget(e.currentTarget);
                    }
                  }}
                  onMouseLeave={() => {
                    if (railCollapsed) {
                      setTooltipContent(null);
                      setTooltipTarget(null);
                    }
                  }}
                >
                  <span className="tenantNavIcon">
                    <IconIssues size={18} />
                  </span>
                  {!railCollapsed && <span className="tenantNavLabel">Issues</span>}
                </NavLink>
                {/* ADR-0214 Part 2 — Activity: every person (the hub shows each the people they may see). */}
                <NavLink
                  to="/app/activity"
                  className="tenantNavRow"
                  title="Activity"
                  onMouseEnter={(e) => {
                    if (railCollapsed) {
                      setTooltipContent("Activity");
                      setTooltipTarget(e.currentTarget);
                    }
                  }}
                  onMouseLeave={() => {
                    if (railCollapsed) {
                      setTooltipContent(null);
                      setTooltipTarget(null);
                    }
                  }}
                >
                  <span className="tenantNavIcon">
                    <IconSubjects size={18} />
                  </span>
                  {!railCollapsed && <span className="tenantNavLabel">Activity</span>}
                </NavLink>
                {/* ADR-0185 Addendum 2 — Optimize: builder/admin always; every other role
                    once the tenant has an active model they can read (content-driven, from
                    the bootstrap probe). Their way in is the Boards tab. */}
                {(props.tenantEntitlement === "builder" ||
                  props.tenantEntitlement === "admin" ||
                  props.optimizeAvailable) && (
                  <NavLink
                    to="/app/optimize"
                    className="tenantNavRow"
                    title="Optimize"
                    onMouseEnter={(e) => {
                      if (railCollapsed) {
                        setTooltipContent("Optimize");
                        setTooltipTarget(e.currentTarget);
                      }
                    }}
                    onMouseLeave={() => {
                      if (railCollapsed) {
                        setTooltipContent(null);
                        setTooltipTarget(null);
                      }
                    }}
                  >
                    <span className="tenantNavIcon">
                      <IconOptimize size={18} />
                    </span>
                    {!railCollapsed && <span className="tenantNavLabel">Optimize</span>}
                  </NavLink>
                )}
              </div>

              {/* Bottom section - Admin (if admin), Account, Help, Sign out */}
              <div className="tenantNavSection tenantNavSectionBottom">
                <div className="tenantNavDivider" />
                {props.tenantEntitlement === "admin" && (
                  <>
                    {!railCollapsed && (
                      <div className="tenantNavGroupHeader" style={{ padding: "8px 12px", fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Admin
                      </div>
                    )}
                    {/* Tenant sub-group: Users & Teams, Connectors, Settings */}
                    {!railCollapsed ? (
                      <>
                        <button
                          type="button"
                          className="tenantNavRow"
                          onClick={() => {
                            setTenantAdminExpanded((v) => {
                              const next = !v;
                              try { localStorage.setItem(getStorageKey("tenantAdminExpanded"), next ? "1" : "0"); } catch { /* ignore */ }
                              return next;
                            });
                          }}
                          aria-expanded={tenantAdminExpanded}
                          style={{ textAlign: "left" }}
                        >
                          <span className="tenantNavIcon"><IconBuilding size={18} /></span>
                          <span className="tenantNavLabel" style={{ flex: 1 }}>Tenant</span>
                          <IconChevron size={16} expanded={tenantAdminExpanded} />
                        </button>
                        {tenantAdminExpanded && (
                          <div className="tenantNavChildren">
                            <NavLink to="/app/admin/users" className="tenantNavRow tenantNavSubRow" title="Users & Teams">
                              <span className="tenantNavLabel">Users & Teams</span>
                            </NavLink>
                            <NavLink to="/app/admin/customers" className="tenantNavRow tenantNavSubRow" title="Account Coverage">
                              <span className="tenantNavLabel">Account Coverage</span>
                            </NavLink>
                            <NavLink to="/app/admin/connectors" className="tenantNavRow tenantNavSubRow" title="Connectors">
                              <span className="tenantNavLabel">Connectors</span>
                            </NavLink>
                            <NavLink to="/app/admin/integrations" className="tenantNavRow tenantNavSubRow" title="Integrations">
                              <span className="tenantNavLabel">Integrations</span>
                            </NavLink>
                            <NavLink to="/app/admin/settings" className="tenantNavRow tenantNavSubRow" title="Settings">
                              <span className="tenantNavLabel">Settings</span>
                            </NavLink>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <NavLink to="/app/admin/users" className="tenantNavRow" title="Users & Teams"
                          onMouseEnter={(e) => { setTooltipContent("Users & Teams"); setTooltipTarget(e.currentTarget); }}
                          onMouseLeave={() => { setTooltipContent(null); setTooltipTarget(null); }}>
                          <span className="tenantNavIcon"><IconTeam size={18} /></span>
                        </NavLink>
                        <NavLink to="/app/admin/customers" className="tenantNavRow" title="Account Coverage"
                          onMouseEnter={(e) => { setTooltipContent("Account Coverage"); setTooltipTarget(e.currentTarget); }}
                          onMouseLeave={() => { setTooltipContent(null); setTooltipTarget(null); }}>
                          <span className="tenantNavIcon"><IconTeam size={18} /></span>
                        </NavLink>
                        <NavLink to="/app/admin/connectors" className="tenantNavRow" title="Connectors"
                          onMouseEnter={(e) => { setTooltipContent("Connectors"); setTooltipTarget(e.currentTarget); }}
                          onMouseLeave={() => { setTooltipContent(null); setTooltipTarget(null); }}>
                          <span className="tenantNavIcon"><IconConnector size={18} /></span>
                        </NavLink>
                        <NavLink to="/app/admin/integrations" className="tenantNavRow" title="Integrations"
                          onMouseEnter={(e) => { setTooltipContent("Integrations"); setTooltipTarget(e.currentTarget); }}
                          onMouseLeave={() => { setTooltipContent(null); setTooltipTarget(null); }}>
                          <span className="tenantNavIcon"><IconConnector size={18} /></span>
                        </NavLink>
                        <NavLink to="/app/admin/settings" className="tenantNavRow" title="Settings"
                          onMouseEnter={(e) => { setTooltipContent("Settings"); setTooltipTarget(e.currentTarget); }}
                          onMouseLeave={() => { setTooltipContent(null); setTooltipTarget(null); }}>
                          <span className="tenantNavIcon"><IconAdmin size={18} /></span>
                        </NavLink>
                      </>
                    )}
                    {/* Content sub-group: Action Item Codes, Escalation Policies, Import/Export, Orphaned Content */}
                    {!railCollapsed ? (
                      <>
                        <button
                          type="button"
                          className="tenantNavRow"
                          onClick={() => {
                            setContentAdminExpanded((v) => {
                              const next = !v;
                              try { localStorage.setItem(getStorageKey("contentAdminExpanded"), next ? "1" : "0"); } catch { /* ignore */ }
                              return next;
                            });
                          }}
                          aria-expanded={contentAdminExpanded}
                          style={{ textAlign: "left" }}
                        >
                          <span className="tenantNavIcon"><IconBuild size={18} /></span>
                          <span className="tenantNavLabel" style={{ flex: 1 }}>Content</span>
                          <IconChevron size={16} expanded={contentAdminExpanded} />
                        </button>
                        {contentAdminExpanded && (
                          <div className="tenantNavChildren">
                            <NavLink to="/app/admin/action-item-codes" className="tenantNavRow tenantNavSubRow" title="Action Item Codes">
                              <span className="tenantNavLabel">Action Item Codes</span>
                            </NavLink>
                            <NavLink to="/app/admin/escalation-policies" className="tenantNavRow tenantNavSubRow" title="Escalation Policies">
                              <span className="tenantNavLabel">Escalation Policies</span>
                            </NavLink>
                            <NavLink to="/app/admin/notifications" className="tenantNavRow tenantNavSubRow" title="Notifications">
                              <span className="tenantNavLabel">Notifications</span>
                            </NavLink>
                            <NavLink to="/app/build/content-transfer" className="tenantNavRow tenantNavSubRow" title="Import / Export">
                              <span className="tenantNavLabel">Import / Export</span>
                            </NavLink>
                            <NavLink to="/app/admin/orphaned-content" className="tenantNavRow tenantNavSubRow" title="Orphaned Content">
                              <span className="tenantNavLabel">Orphaned Content</span>
                            </NavLink>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <NavLink to="/app/admin/action-item-codes" className="tenantNavRow" title="Action Item Codes"
                          onMouseEnter={(e) => { setTooltipContent("Action Item Codes"); setTooltipTarget(e.currentTarget); }}
                          onMouseLeave={() => { setTooltipContent(null); setTooltipTarget(null); }}>
                          <span className="tenantNavIcon"><IconKnowledge size={18} /></span>
                        </NavLink>
                        <NavLink to="/app/admin/escalation-policies" className="tenantNavRow" title="Escalation Policies"
                          onMouseEnter={(e) => { setTooltipContent("Escalation Policies"); setTooltipTarget(e.currentTarget); }}
                          onMouseLeave={() => { setTooltipContent(null); setTooltipTarget(null); }}>
                          <span className="tenantNavIcon"><IconKnowledge size={18} /></span>
                        </NavLink>
                        <NavLink to="/app/admin/notifications" className="tenantNavRow" title="Notifications"
                          onMouseEnter={(e) => { setTooltipContent("Notifications"); setTooltipTarget(e.currentTarget); }}
                          onMouseLeave={() => { setTooltipContent(null); setTooltipTarget(null); }}>
                          <span className="tenantNavIcon"><IconKnowledge size={18} /></span>
                        </NavLink>
                        <NavLink to="/app/build/content-transfer" className="tenantNavRow" title="Import / Export"
                          onMouseEnter={(e) => { setTooltipContent("Import / Export"); setTooltipTarget(e.currentTarget); }}
                          onMouseLeave={() => { setTooltipContent(null); setTooltipTarget(null); }}>
                          <span className="tenantNavIcon"><IconTransfer size={18} /></span>
                        </NavLink>
                        <NavLink to="/app/admin/orphaned-content" className="tenantNavRow" title="Orphaned Content"
                          onMouseEnter={(e) => { setTooltipContent("Orphaned Content"); setTooltipTarget(e.currentTarget); }}
                          onMouseLeave={() => { setTooltipContent(null); setTooltipTarget(null); }}>
                          <span className="tenantNavIcon"><IconOrphaned size={18} /></span>
                        </NavLink>
                      </>
                    )}
                    {/* ADR-0068 — AI sub-group (collapsible) */}
                    {!railCollapsed ? (
                      <>
                        <button
                          type="button"
                          className="tenantNavRow"
                          onClick={() => {
                            setAiAdminExpanded((v) => {
                              const next = !v;
                              try { localStorage.setItem(getStorageKey("aiAdminExpanded"), next ? "1" : "0"); } catch { /* ignore */ }
                              return next;
                            });
                          }}
                          aria-expanded={aiAdminExpanded}
                          style={{ textAlign: "left" }}
                        >
                          <span className="tenantNavIcon"><IconKnowledge size={18} /></span>
                          <span className="tenantNavLabel" style={{ flex: 1 }}>AI</span>
                          <IconChevron size={16} expanded={aiAdminExpanded} />
                        </button>
                        {aiAdminExpanded && (
                          <div className="tenantNavChildren">
                            {/* ADR-0148: Tenant Knowledge is the single knowledge surface; Learning is
                                its analytics sub-view (reached via its breadcrumb) — keep Tenant
                                Knowledge highlighted while on it. The legacy "AI Knowledge" page retired. */}
                            <NavLink
                              to="/app/admin/tenant-knowledge"
                              className={`tenantNavRow tenantNavSubRow ${location.pathname.startsWith("/app/admin/learning") ? "tenantNavActive" : ""}`}
                              title="Tenant Knowledge"
                            >
                              <span className="tenantNavLabel">Tenant Knowledge</span>
                            </NavLink>
                            <NavLink to="/app/admin/ai-usage" className="tenantNavRow tenantNavSubRow" title="AI Usage">
                              <span className="tenantNavLabel">AI Usage</span>
                            </NavLink>
                          </div>
                        )}
                      </>
                    ) : (
                      // Rail-collapsed mode: render AI entries flat with icon tooltips
                      <>
                        <NavLink to="/app/admin/tenant-knowledge"
                          className={`tenantNavRow ${location.pathname.startsWith("/app/admin/learning") ? "tenantNavActive" : ""}`}
                          title="Tenant Knowledge"
                          onMouseEnter={(e) => { setTooltipContent("Tenant Knowledge"); setTooltipTarget(e.currentTarget); }}
                          onMouseLeave={() => { setTooltipContent(null); setTooltipTarget(null); }}>
                          <span className="tenantNavIcon"><IconTenantKnowledge size={18} /></span>
                        </NavLink>
                        <NavLink to="/app/admin/ai-usage" className="tenantNavRow" title="AI Usage"
                          onMouseEnter={(e) => { setTooltipContent("AI Usage"); setTooltipTarget(e.currentTarget); }}
                          onMouseLeave={() => { setTooltipContent(null); setTooltipTarget(null); }}>
                          <span className="tenantNavIcon"><IconUsage size={18} /></span>
                        </NavLink>
                      </>
                    )}
                  </>
                )}
                <NavLink
                  to="/app/account"
                  className="tenantNavRow"
                  title="Account"
                  onMouseEnter={(e) => {
                    if (railCollapsed) {
                      setTooltipContent("Account");
                      setTooltipTarget(e.currentTarget);
                    }
                  }}
                  onMouseLeave={() => {
                    if (railCollapsed) {
                      setTooltipContent(null);
                      setTooltipTarget(null);
                    }
                  }}
                >
                  <span className="tenantNavIcon">
                    <IconAccount size={18} />
                  </span>
                  {!railCollapsed && <span className="tenantNavLabel">Account</span>}
                </NavLink>
                <NavLink
                  to="/app/help"
                  className="tenantNavRow"
                  title="Help/Docs"
                  onMouseEnter={(e) => {
                    if (railCollapsed) {
                      setTooltipContent("Help/Docs");
                      setTooltipTarget(e.currentTarget);
                    }
                  }}
                  onMouseLeave={() => {
                    if (railCollapsed) {
                      setTooltipContent(null);
                      setTooltipTarget(null);
                    }
                  }}
                >
                  <span className="tenantNavIcon">
                    <IconHelp size={18} />
                  </span>
                  {!railCollapsed && <span className="tenantNavLabel">Help/Docs</span>}
                </NavLink>
                <button
                  type="button"
                  className="tenantNavRow tenantNavSignOut"
                  onClick={() => void logout()}
                  title="Sign out"
                  onMouseEnter={(e) => {
                    if (railCollapsed) {
                      setTooltipContent("Sign out");
                      setTooltipTarget(e.currentTarget);
                    }
                  }}
                  onMouseLeave={() => {
                    if (railCollapsed) {
                      setTooltipContent(null);
                      setTooltipTarget(null);
                    }
                  }}
                >
                  <span className="tenantNavIcon">
                    <IconSignOut size={18} />
                  </span>
                  {!railCollapsed && <span className="tenantNavLabel">Sign out</span>}
                </button>
              </div>
              {/* Tooltip for rail mode */}
              {railCollapsed && tooltipContent && tooltipTarget && (
                <div
                  className="tenantNavTooltip"
                  style={{
                    top: tooltipTarget.offsetTop + tooltipTarget.offsetHeight / 2,
                  }}
                >
                  {tooltipContent}
                </div>
              )}
            </>
          ) : (
            props.navItems.map((it) =>
              "group" in it ? (
                <div className="navGroup" key={it.group}>
                  <div className="navGroupLabel">{it.group}</div>
                  {it.items.map((item) => (
                    <NavLink key={item.to} to={item.to}>
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              ) : (
                <NavLink key={it.to} to={it.to}>
                  {it.label}
                </NavLink>
              )
            )
          )}
        </aside>
        <main>{props.children}</main>
      </div>
    </div>
  );
}

